import { useMemo, useState } from "react";
import { Landmark, Wallet, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { clp } from "@/lib/api";
import { FinanciamientoModal } from "@/components/aliados/FinanciamientoModal";

export const cuotaMensual = (monto, tasaPct, plazo) => {
  if (monto <= 0 || plazo <= 0) return 0;
  const i = tasaPct / 100;
  if (i === 0) return Math.round(monto / plazo);
  return Math.round((monto * i) / (1 - Math.pow(1 + i, -plazo)));
};

/* Pre-aprobación referencial del aliado financiero: cuota mensual junto al valor del auto. */
export const CuotaEstimada = ({ financiador, valor, patente = "", origen = "portal", compact = false }) => {
  const p = financiador?.params || {};
  const [piePct, setPiePct] = useState(p.pie_pct ?? 20);
  const [plazo, setPlazo] = useState(p.plazo_default ?? 48);
  const [open, setOpen] = useState(false);
  const color = financiador?.color || "#13315C";

  const calc = useMemo(() => {
    const v = Number(valor) || 0;
    const pie = Math.round((v * piePct) / 100);
    const monto = Math.max(0, v - pie);
    const cuota = cuotaMensual(monto, p.tasa_mensual_pct ?? 1.49, plazo);
    return { v, pie, monto, cuota, ingreso: cuota ? Math.round((cuota * 100) / (p.ingreso_ratio_pct || 25)) : 0, ok: monto >= (p.monto_min || 0) };
  }, [valor, piePct, plazo, p]);

  if (!financiador || !calc.v) return null;

  const extra = { pie: calc.pie, plazo_meses: plazo, cuota_estimada: calc.cuota, monto: calc.monto };

  if (compact) {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} data-testid="cuota-compact"
          className="w-full mt-2 rounded-md border-2 px-3 py-2.5 text-left transition-colors hover:bg-slate-50 bg-white"
          style={{ borderColor: color }}>
          <span className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color }}><Landmark size={15} /> {financiador.texto}</span>
            <span className="text-xs text-slate-500">desde</span>
          </span>
          <span className="flex items-baseline justify-between gap-2 mt-0.5">
            <span className="text-[11px] text-slate-500">pie {piePct}% · {plazo} meses · pre-aprobación en línea</span>
            <span className="font-display font-bold text-lg text-[#13315C]" data-testid="cuota-compact-valor">{clp(calc.cuota)}<span className="text-xs font-normal text-slate-500">/mes</span></span>
          </span>
        </button>
        {open && <FinanciamientoModal financiador={financiador} patente={patente} valor={calc.v} extra={extra} origen={origen} onClose={() => setOpen(false)} />}
      </>
    );
  }

  return (
    <div className="rounded-2xl border bg-white p-5 sm:p-6 shadow-sm" style={{ borderColor: `${color}55` }} data-testid="cuota-estimada">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wide font-bold" style={{ color }}>Pre-aprobación referencial · {financiador.nombre}</p>
          <h3 className="font-display font-bold text-xl sm:text-2xl text-[#13315C] mt-1">¿Te alcanza? Cuota mensual estimada</h3>
        </div>
        <div className="text-right">
          <p className="font-display font-bold text-3xl text-[#13315C]" data-testid="cuota-valor">{clp(calc.cuota)}<span className="text-sm font-normal text-slate-500">/mes</span></p>
          <p className="text-[11px] text-slate-500">{plazo} meses · tasa {p.tasa_mensual_pct}% mensual</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        <div>
          <div className="flex justify-between text-xs text-slate-600 mb-1">
            <span>Pie inicial</span><b className="text-[#13315C]">{piePct}% · {clp(calc.pie)}</b>
          </div>
          <input type="range" min={0} max={70} step={5} value={piePct} onChange={(e) => setPiePct(Number(e.target.value))}
            className="w-full h-2 accent-[#13315C]" style={{ accentColor: color }} data-testid="cuota-pie-slider" />
          <p className="text-[11px] text-slate-500 mt-1">Monto a financiar: <b>{clp(calc.monto)}</b></p>
        </div>
        <div>
          <p className="text-xs text-slate-600 mb-1.5">Plazo</p>
          <div className="flex flex-wrap gap-1.5">
            {(p.plazos || [24, 36, 48, 60]).map((m) => (
              <button key={m} type="button" onClick={() => setPlazo(m)} data-testid={`cuota-plazo-${m}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${plazo === m ? "text-white" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                style={plazo === m ? { background: color, borderColor: color } : undefined}>
                {m} meses
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1"><Wallet size={12} /> Ingreso mensual sugerido: <b className="text-[#13315C]" data-testid="cuota-ingreso">{clp(calc.ingreso)}</b></p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-5">
        <Button onClick={() => setOpen(true)} disabled={!calc.ok} className="h-12 px-6 text-white font-semibold" style={{ background: color }} data-testid="cuota-preaprobar-btn">
          <Landmark size={16} className="mr-1.5" /> Solicitar pre-aprobación con {financiador.nombre}
        </Button>
        {!calc.ok && <span className="text-xs text-amber-700">El monto mínimo a financiar es {clp(p.monto_min)}.</span>}
      </div>
      <p className="text-[11px] text-slate-400 mt-3 flex gap-1.5"><Info size={12} className="shrink-0 mt-0.5" /> Simulación referencial; no constituye oferta ni pre-aprobación formal. Sujeta a evaluación crediticia de {financiador.nombre}.</p>

      {open && <FinanciamientoModal financiador={financiador} patente={patente} valor={calc.v} extra={extra} origen={origen} onClose={() => setOpen(false)} />}
    </div>
  );
};
